import React from 'react';
import {FormattedMessage, useIntl} from 'react-intl';
import {FormikProvider} from 'formik';
import {StyledFieldInput} from '../../../_common/components/FieldInput';
import FieldDatepicker from '../../../_common/components/FieldDatepicker';
import FieldCheckbox, {InputCheckbox} from '../../../_common/components/FieldCheckbox';
import FieldFindCustomers from '../../../_common/components/FieldFindCustomers';
import PopupAddCustomer from '../../../CustomersEntity/components/PopupAddCustomer';
import FieldFindContacts from '../../../_common/components/FieldFindContacts';
import {StyledFieldSelect} from '../../../_common/components/FieldSelect';
import {integerValue, formatStringToFloatNumber} from '../../../_common/utils/changeInputValue';
import {useOrderDetailsUIGlobalContext} from '../OrderDetailsUIGlobalContext';
import {useOrderDetailsUIOrderContext} from '../OrderDetailsUIOrderContext';
import {useOrderDetailsUIInspectionsListContext} from '../OrderDetailsUIInspectionsListContext';
import {filterOptions} from '../../../_common/utils/filterOptions';
import PopupAddContact from '../../../ContactsPage/components/PopupAddContact';
import ButtonAction from '../../../_common/components/ButtonAction';
import Unit from '../../../_common/components/Unit';
import Spinner from '../../../_common/components/Spinner';
import styles from '../styles.module.scss';

const TabOrderGeneral = () => {
  const intl = useIntl();
  const {orderID, users} = useOrderDetailsUIGlobalContext();
  const {
    formikOrderGeneral,
    handleSubmit,
    oldInspectionsForms,
    toggleOldInspectionForms,
    orderDetailsLoading
  } = useOrderDetailsUIOrderContext();
  const {formikOrderInspections} = useOrderDetailsUIInspectionsListContext();
  const oldInspectionsDisabled = (orderID !== -1)
    && (formikOrderInspections?.values.inspections.filter(item => item.shumaID > 0).length > 0);
  const {
    values,
    setFieldValue,
    handleChange,
    isValid
  } = formikOrderGeneral;
  const prefix = 'tab-order-general';

  return orderDetailsLoading ? <Spinner customContainerStyles={'d-flex justify-content-center'}/> : (
    <FormikProvider value={formikOrderGeneral}>
      <div className={styles.tabOrderGeneralContainer}>
        <Unit
          text={intl.formatMessage({id: 'details', defaultMessage: 'Details'})}
          icon={'/media/common/Warning_circle_navy.svg'}
        >
          <div className={styles.fieldsLine}>
            <StyledFieldInput
              fieldId={'shumaForOrderCount'}
              label={
                <>
                  <FormattedMessage id={`${prefix}-field-label-shumaForOrderCount`} defaultMessage={'Shuma for order count'}/>
                  <span className={'text-danger'}>*</span>
                </>
              }
              value={values.shumaForOrderCount}
              onChange={e => setFieldValue('shumaForOrderCount', integerValue(e))}
              icon={'/media/common/List_primary.svg'}
              customContainerStyles={'w-50 mr-4'}
            />
            <FieldDatepicker
              fieldId={'dueDate'}
              label={<FormattedMessage id={`${prefix}-field-label-dueDate`} defaultMessage={'Due date'}/>}
              onChange={(date) => {
                setFieldValue('dueDate', new Date(date));
              }}
              placeholder={''}
              customContainerStyles={'w-50'}
            />
          </div>
          <div className={styles.fieldsLine}>
            <StyledFieldSelect
              name={'responsibleUser'}
              options={users}
              value={values.responsibleUser}
              onChange={({value}) => {
                setFieldValue('responsibleUser', filterOptions(value)(users)[0]);
              }}
              isSearchable={true}
              isAsync={true}
              label={<FormattedMessage id={`${prefix}-field-label-responsibleUser`} defaultMessage={'Firm users'}/>}
              customContainerStyles={'w-50 mr-4'}
            />
            <InputCheckbox
              label={<FormattedMessage id={`${prefix}-field-label-oldInspections`} defaultMessage={'Old inspections'}/>}
              value={oldInspectionsForms}
              onChange={toggleOldInspectionForms}
              disabled={oldInspectionsDisabled}
              customContainerStyles={'mt-6 w-50'}
            />
          </div>
          <div className={styles.fieldsLine}>
            <StyledFieldInput
              fieldId={'paid'}
              label={<FormattedMessage id={`${prefix}-field-label-paid`} defaultMessage={'Paid'}/>}
              value={values.paid}
              onChange={e => setFieldValue('paid', formatStringToFloatNumber(e))}
              icon={'/media/common/Money_primary.svg'}
              customContainerStyles={'w-50 mr-4'}
            />
            <StyledFieldInput
              fieldId={'totalPayment'}
              label={<FormattedMessage id={`${prefix}-field-label-totalPayment`} defaultMessage={'Total payment'}/>}
              value={values.totalPayment}
              onChange={e => setFieldValue('totalPayment', formatStringToFloatNumber(e))}
              icon={'/media/common/Money_primary.svg'}
              customContainerStyles={'w-50'}
            />
          </div>
          <div className={styles.fieldsLine}>
            <FieldCheckbox
              fieldId="usingLawyer"
              label={<FormattedMessage id={`${prefix}-field-label-usingLawyer`} defaultMessage={'Using lawyer'}/>}
              value={values.usingLawyer}
              customContainerStyles={'w-50 mt-6 mr-4'}
            />
            <StyledFieldInput
              fieldId={'lawyerName'}
              label={<FormattedMessage id={`${prefix}-field-label-lawyerName`} defaultMessage={'Lawyer name'}/>}
              value={values.lawyerName}
              disabled={!values.usingLawyer}
              onChange={handleChange}
              icon={'/media/common/User_primary.svg'}
              customContainerStyles={'w-50'}
            />
          </div>
        </Unit>
        <Unit
          text={intl.formatMessage({id: 'customers', defaultMessage: 'Customers'})}
          icon={'/media/common/Group_pink.svg'}
        >
          <FieldFindCustomers
            name={'customers'}
            formik={formikOrderGeneral}
            customContainerStyles={'w-100'}
            customItemStyles={'d-flex align-items-center'}
            customLabel={
              <div className={'d-flex justify-content-between align-items-center w-100'}>
                <div>
                  {intl.formatMessage({id: 'typeahead-customer-label', defaultMessage: 'Search customers'})}
                  <span className={'text-danger'}>*</span>
                </div>
                <PopupAddCustomer formikProps={formikOrderGeneral}/>
              </div>
            }
          />
        </Unit>
        <Unit
          text={intl.formatMessage({id: 'contacts', defaultMessage: 'Contacts'})}
          icon={'/media/common/User_orange.svg'}
        >
          <FieldFindContacts
            name={'contacts'}
            formik={formikOrderGeneral}
            customLabel={
              <div className={'d-flex justify-content-between align-items-center w-100'}>
                {intl.formatMessage({id: 'typeahead-contact-label', defaultMessage: 'Search contact'})}
                <PopupAddContact formikProps={formikOrderGeneral}/>
              </div>
            }
            customContainerStyles={'w-100'}
          />
        </Unit>
      </div>
      <ButtonAction
        useIcon={'/media/common/Check-in-circle_white.svg'}
        label={orderID === -1
          ? <FormattedMessage id={`${prefix}-button-label-create-order`} defaultMessage={'Create an order'}/>
          : <FormattedMessage id={`${prefix}-button-label-edit-order`} defaultMessage={'Edit an order'}/>
        }
        onClick={() => handleSubmit({...values, orderID})}
        disabled={!isValid}
        customClassnames={'d-block mt-8 mx-auto'}
      />
    </FormikProvider>
  );
};

export default TabOrderGeneral;
